import { Card, CardContent, cn } from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type ExpenseStatTone =
  | "default"
  | "active"
  | "inactive"
  | "pending"
  | "approved"
  | "paid";

const toneStyles: Record<
  ExpenseStatTone,
  { icon: string; hover: string; active: string }
> = {
  default: {
    icon: "bg-primary/10 text-primary",
    hover: "hover:border-primary/30 hover:bg-primary/5",
    active: "border-primary/50 bg-primary/5 ring-1 ring-primary/30",
  },
  active: {
    icon: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    hover: "hover:border-emerald-500/30 hover:bg-emerald-500/5",
    active: "border-emerald-500/40 bg-emerald-500/5 ring-1 ring-emerald-500/25",
  },
  inactive: {
    icon: "bg-muted text-muted-foreground",
    hover: "hover:border-border hover:bg-muted/40",
    active: "border-border bg-muted/30 ring-1 ring-border",
  },
  pending: {
    icon: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    hover: "hover:border-amber-500/30 hover:bg-amber-500/5",
    active: "border-amber-500/40 bg-amber-500/5 ring-1 ring-amber-500/25",
  },
  approved: {
    icon: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    hover: "hover:border-emerald-500/30 hover:bg-emerald-500/5",
    active: "border-emerald-500/40 bg-emerald-500/5 ring-1 ring-emerald-500/25",
  },
  paid: {
    icon: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    hover: "hover:border-sky-500/30 hover:bg-sky-500/5",
    active: "border-sky-500/40 bg-sky-500/5 ring-1 ring-sky-500/25",
  },
};

type ExpenseStatCardProps = {
  title: string;
  value: ReactNode;
  meta?: string;
  metaAccent?: boolean;
  isLoading: boolean;
  icon: LucideIcon;
  tone: ExpenseStatTone;
  active?: boolean;
  onClick?: () => void;
};

export function ExpenseStatCard({
  title,
  value,
  meta,
  metaAccent = false,
  isLoading,
  icon: Icon,
  tone,
  active,
  onClick,
}: ExpenseStatCardProps) {
  const styles = toneStyles[tone];
  const interactive = !!onClick;

  return (
    <Card
      className={cn(
        "transition-colors",
        interactive && "cursor-pointer",
        interactive && !active && styles.hover,
        active && styles.active,
      )}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick?.();
            }
          }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <CardContent className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <p className="text-muted-foreground text-sm">{title}</p>
          <p className="font-semibold text-3xl tabular-nums tracking-tight">
            {isLoading ? "—" : value}
          </p>
          {meta ? (
            <p
              className={cn(
                "font-medium text-xs",
                metaAccent
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-foreground/80",
              )}
            >
              {meta}
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl",
            styles.icon,
          )}
        >
          <Icon className="size-5" aria-hidden />
        </div>
      </CardContent>
    </Card>
  );
}
